import { useEffect, useSyncExternalStore } from "react";
import { api } from "./api.js";
import { useRealtimeFrames } from "./realtime-context.js";

/**
 * Unsent composer text, per channel and per thread, kept on the server so it
 * follows the person between devices.
 *
 * The store is module-level because the composer is remounted by key whenever
 * the channel or thread changes: the text has to outlive the component to be
 * restored on return. A composer reads its draft once, when it mounts, and
 * writes through `saveDraft` as the person types — so a frame arriving from
 * another device never overwrites what is being typed in the open composer.
 */

const SAVE_DELAY_MS = 800;

type Listener = () => void;

const drafts = new Map<string, string>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();
const listeners = new Set<Listener>();
/** The set of keys, as a stable snapshot for useSyncExternalStore. */
let snapshot: ReadonlySet<string> = new Set();

export function draftKey(channelId: string, threadRootId?: string | null): string {
  return `${channelId}:${threadRootId ?? ""}`;
}

function emit() {
  snapshot = new Set(drafts.keys());
  for (const listener of listeners) listener();
}

function setLocal(key: string, body: string | null) {
  const had = drafts.has(key);
  if (body === null || body.trim() === "") {
    if (!had) return;
    drafts.delete(key);
  } else {
    drafts.set(key, body);
    if (had) return; // the key set is unchanged, so nobody needs to re-render
  }
  emit();
}

export function getDraft(channelId: string, threadRootId?: string | null): string {
  return drafts.get(draftKey(channelId, threadRootId)) ?? "";
}

function flush(channelId: string, threadRootId: string | null, body: string) {
  // Best effort: a draft that fails to sync is still kept on this device.
  api
    .put(`/channels/${channelId}/draft`, { body, ...(threadRootId ? { threadRootId } : {}) })
    .catch(() => undefined);
}

/** Records the composer's text and syncs it once typing pauses. */
export function saveDraft(channelId: string, threadRootId: string | null, body: string): void {
  const key = draftKey(channelId, threadRootId);
  setLocal(key, body);
  const pending = timers.get(key);
  if (pending) clearTimeout(pending);
  timers.set(
    key,
    setTimeout(() => {
      timers.delete(key);
      flush(channelId, threadRootId, body);
    }, SAVE_DELAY_MS),
  );
}

/**
 * Forgets a draft once its message has been sent. The server clears its own
 * copy as part of the send, so nothing is requested here — only the debounced
 * save that would otherwise resurrect it is cancelled.
 */
export function discardDraft(channelId: string, threadRootId: string | null): void {
  const key = draftKey(channelId, threadRootId);
  const pending = timers.get(key);
  if (pending) clearTimeout(pending);
  timers.delete(key);
  setLocal(key, null);
}

/** Applies the server's view: a full load, or one `draft` frame. Keys being typed into are left alone. */
export function applyServerDrafts(list: { key: string; body: string }[], replaceAll: boolean): void {
  if (replaceAll) {
    for (const key of [...drafts.keys()]) if (!timers.has(key)) drafts.delete(key);
  }
  for (const { key, body } of list) {
    if (timers.has(key)) continue;
    if (body.trim() === "") drafts.delete(key);
    else drafts.set(key, body);
  }
  emit();
}

export function resetDrafts(): void {
  for (const timer of timers.values()) clearTimeout(timer);
  timers.clear();
  drafts.clear();
  emit();
}

function subscribe(listener: Listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The `channelId:threadRootId` keys that currently hold a draft. */
export function useDraftKeys(): ReadonlySet<string> {
  return useSyncExternalStore(subscribe, () => snapshot);
}

/** Whether the channel's own composer (not a thread's) has an unsent draft. */
export function hasChannelDraft(keys: ReadonlySet<string>, channelId: string): boolean {
  return keys.has(draftKey(channelId, null));
}

type DraftRow = { channelId: string; threadRootId: string | null; body: string };

/**
 * Mount once, in the chat view: loads the viewer's drafts and keeps them in
 * step with the `draft` frames other devices cause.
 */
export function useDraftSync(): void {
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const rows: DraftRow[] = [];
      let before: string | null = null;
      // Newest first and bounded; a few pages are plenty for a sidebar marker.
      for (let page = 0; page < 5; page++) {
        const res: { drafts: DraftRow[]; nextBefore: string | null } = await api.get(
          `/drafts?limit=100${before ? `&before=${encodeURIComponent(before)}` : ""}`,
        );
        rows.push(...res.drafts);
        before = res.nextBefore;
        if (!before) break;
      }
      if (!cancelled) {
        applyServerDrafts(
          rows.map((d) => ({ key: draftKey(d.channelId, d.threadRootId), body: d.body })),
          true,
        );
      }
    };
    load().catch(() => undefined);
    return () => {
      cancelled = true;
      resetDrafts();
    };
  }, []);

  useRealtimeFrames((frame) => {
    if (frame.t !== "draft") return;
    applyServerDrafts([{ key: draftKey(frame.channelId, frame.threadRootId), body: frame.body ?? "" }], false);
  });
}
